import React, { useEffect, useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import ReducerConstants from '../../../../../_shared/constants/reducers';
import renderLocale from '../../../../../_shared/helpers/renderLocale';
import convertToCurrency from '../../../../../_shared/utils/convertToCurrency';
import { TradeState } from '../../../../../_shared/types/trade';
import SystemConfigService from '../../../../../services/SystemConfigService';
import { BidStatus } from '../../../../../_shared/types/auction';
import { SseEventType } from '../../../../../_shared/constants/sse';

import ActiveTimer from '../../../../../_shared/components/ActiveTimer';
import BidBadge from '../../../../../_shared/components/BidBadge';
import FloorMeter from '../../../../FloorMeter';

import {
  StyledBidStatus,
  StyledFloorPrice,
  StyledCurrentBid,
  StyledBidState,
  StyledBadges,
  StyledBidModalTimer
} from './Styles';

const BiddingStatus = ({ variant, bidContext, floorPrice }) => {
  const {
    activeExpiryTime,
    bidStatus,
    bidList,
    currentBidAmount,
    id,
    isAuctionEnded,
    numberOfBids,
    tradeFloorPrice,
    tradeState,
    isTimerExtended
  } = bidContext ?? {};

  const [user, configurations, liveBid] = useSelector(
    state => [
      state[ReducerConstants.AUTH]?.user,
      state[ReducerConstants.AUTH]?.system?.configurations,
      state[ReducerConstants.LIVE_BIDS][id]
    ],
    shallowEqual
  );

  const [isFloorPriceMet, setIsFloorPriceMet] = useState(false);
  const [hasFloorPrice, setHasFloorPrice] = useState(false);
  const [isCloseToFloorPrice, setIsCloseToFloorPrice] = useState(false);
  const [isAuctionExtended, setIsAuctionExtended] = useState(false);
  const systemConfigService = SystemConfigService.get(configurations);
  const userLocale = renderLocale(user?.auth);
  const localeVariant = userLocale ? userLocale.substring(0, 2) : null;
  const hasBids = Number.isInteger(numberOfBids);

  useEffect(() => {
    if (tradeFloorPrice) {
      setIsFloorPriceMet(tradeFloorPrice.isFloorPriceMet);
      setHasFloorPrice(tradeFloorPrice.hasFloorPrice || false);
      setIsCloseToFloorPrice(tradeFloorPrice.isCloseToFloorPrice || false);
    }
  }, [tradeFloorPrice]);

  useEffect(() => {
    setIsAuctionExtended(isTimerExtended);
  }, [isTimerExtended]);

  /**
   * Live Bid
   */
  useEffect(() => {
    if (liveBid && liveBid?.eventType === SseEventType.BID_EVENT) {
      setIsFloorPriceMet(liveBid.isFloorPriceMet);
      setHasFloorPrice(liveBid.hasFloorPrice || false);
      setIsCloseToFloorPrice(liveBid.isCloseToFloorPrice || false);
    }
  }, [liveBid]);

  const CurrentBidPriceComponent = () => {
    const variantIsAuctionEnded = isAuctionEnded ? 'auctionEnded' : null;
    return numberOfBids > 0 ? (
      <StyledCurrentBid
        data-testid="current-bid-value"
        variant={variantIsAuctionEnded}
      >
        {variant !== isAuctionEnded && (
          <StyledCurrentBid.CurrentBid>
            {/* Current bid */}
            <FormattedMessage id="vdp.bid.currentBid" />
          </StyledCurrentBid.CurrentBid>
        )}

        {convertToCurrency(currentBidAmount || bidList?.bids?.[0]?.amount)}
      </StyledCurrentBid>
    ) : (
      <StyledCurrentBid.NoBidsMessage
        variant={variant}
        localeVariant={localeVariant}
        data-testid="current-bid-value"
      >
        {/* No bids yet */}
        <FormattedMessage id="vdp.bid.noBids" />
      </StyledCurrentBid.NoBidsMessage>
    );
  };

  return (
    <StyledBidStatus variant={variant}>
      <StyledCurrentBid.Wrapper>
        {hasBids && <CurrentBidPriceComponent />}
      </StyledCurrentBid.Wrapper>

      <StyledBidState variant={variant}>
        <StyledBadges variant={variant}>
          <BiddingStatusFloorPrice floorPrice={floorPrice} />
          {(bidStatus === BidStatus.topBidder ||
            bidStatus === BidStatus.proxy) && (
            <BidBadge testId="bid-status" variant="success">
              {/* High bid! */}
              <FormattedMessage id="vdp.highbid" />
            </BidBadge>
          )}
          {bidStatus === BidStatus.outbid && (
            <BidBadge testId="bid-status" variant="alert">
              {/* Outbid! */}
              <FormattedMessage id="vdp.outbid" />
            </BidBadge>
          )}
          <FloorMeter
            variant={variant}
            isFloorPriceMet={isFloorPriceMet}
            hasFloorPrice={hasFloorPrice}
            isCloseToFloorPrice={isCloseToFloorPrice}
          />
        </StyledBadges>
        {variant === 'modal' &&
          tradeState === TradeState.active &&
          systemConfigService.activeTimerFeatureEnabled() && (
            <StyledBidModalTimer>
              <ActiveTimer
                onAuctionEnded={() => null}
                activeExpiryTime={activeExpiryTime}
                tradeId={id}
                isTimerExtended={isAuctionExtended}
              />
            </StyledBidModalTimer>
          )}
      </StyledBidState>
    </StyledBidStatus>
  );
};

export const BiddingStatusFloorPrice = ({ floorPrice, floorPriceVariant }) => {
  const [user] = useSelector(
    state => [state[ReducerConstants.AUTH]?.user],
    shallowEqual
  );
  const userLocale = renderLocale(user?.auth);
  const localeVariant = userLocale ? userLocale.substring(0, 2) : null;
  return floorPrice ? (
    <StyledFloorPrice variant={floorPriceVariant || localeVariant}>
      <FormattedMessage
        id="vdp.bid.floorPrice"
        values={{ floorPrice: convertToCurrency(floorPrice) }}
      />
    </StyledFloorPrice>
  ) : null;
};

export default BiddingStatus;
